'use client'

import React, { useEffect, useRef } from 'react';
// import { X } from '@phosphor-icons/react';
import LPForm from '@/components/LPForm/LPForm';
import { LanguageType } from '@/lib/types';
import { usePreventScroll } from '@/hooks/usePreventScroll';
import { debug } from '@/lib/debug';
import { useIsLg } from '@/contexts/BreakpointContext';
interface SlidingFormSectionProps {
  isOpen: boolean;
  onClose: () => void;
  language: LanguageType;
  headlineText?: string;
  subheadingText?: string;
  benefits?: string[];
}

const SlidingFormSection: React.FC<SlidingFormSectionProps> = ({
  isOpen,
  onClose,
  language,
  headlineText = 'Begin your journey',
  subheadingText = "We book limited daily appointments to ensure the highest level of care.",
  benefits = [
    "Without needles or surgery",
    "Without pain",
    "Without recovery time"
  ]
}) => {
  const isLg = useIsLg();
  const overlayRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Move focus into the dialog when it opens so screen readers announce it
  // (otherwise focus stays on the trigger button and AT just re-reads the
  // trigger). Restore focus to the trigger on close.
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      // Defer to next frame so the dialog's translate-x transition has begun
      // and the element is actually visible/focusable.
      requestAnimationFrame(() => formRef.current?.focus());
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflowY = 'scroll';
  }, []);

  usePreventScroll(isOpen && !isLg);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        ref={overlayRef}
        className={`
          fixed inset-0 bg-transparent backdrop-blur-[6px] z-50 transition-opacity duration-300
          ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
        onClick={handleOverlayClick}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      />

      {/* Sliding Form Container */}
      <div
        ref={formRef}
        role="dialog"
        aria-modal="true"
        aria-label={headlineText}
        aria-hidden={!isOpen || undefined}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={`
          px-[0.5rem] lg:px-[1.5rem]
          fixed top-0 right-0
          h-full w-full tb:w-fit bg-transparent z-50
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
        style={{ direction: language === 'arabic' ? 'rtl' : 'ltr' }}
      >
        {/* Form Content */}
        <div className="h-full w-full flex flex-col justify-center items-center">
          <div
            // overflow-y-auto
            className="
            hide-scrollbar
            w-full
            max-h-full
            tb:h-full
            tb:max-h-[100svh]
            overflow-y-auto
            py-[0.5rem] lg:py-[0.75rem]
            flex flex-col tb:justify-center
            ">
            <LPForm
              useAlternativeLayout={true}
              language={language}
              headlineText={headlineText}
              subheadingText={subheadingText}
              benefits={benefits}
              onSubmit={(formData) => {
                debug.log('Sliding form submitted:', formData);
                // onClose();
              }}
              onClose={onClose}
              borderColor='border-primary-base'
              // shadowClass="shadow-sunshine-medium-glow"
              innerHorizontalPadding="px-0 tb:px-[3.5rem]"
              innerVerticalPadding="pt-0 pb-[1.5rem] tb:pt-[2.5rem] tb:pb-[1.5rem]"
              shouldPreventLenisBehavior={true}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SlidingFormSection;
